export interface TocScrollOptions {
  offset?: number;
  correctionDelayMs?: number;
  correctionDelaysMs?: number[];
  updateHash?: boolean;
}

const DEFAULT_TOC_CLEARANCE_PX = 18;
const POSITION_TOLERANCE_PX = 8;
const MOBILE_ARTICLE_TOC_MAX_WIDTH_PX = 1279;

let activeScrollSession = 0;

function toPx(value: string | null | undefined, fallback = 0): number {
  if (!value) return fallback;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function isMobileArticleTocViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_ARTICLE_TOC_MAX_WIDTH_PX;
}

/** Clearance through the sticky mobile article TOC (blog/review pages below xl). */
function getMobileArticleTocClearance(minGap = 16): number {
  if (typeof document === 'undefined' || typeof window === 'undefined') return 0;
  if (!isMobileArticleTocViewport()) return 0;

  const mobileToc = document.querySelector('.mobile-toc') as HTMLElement | null;
  if (!mobileToc) return 0;

  const stickyTop = toPx(window.getComputedStyle(mobileToc).top, 0);
  const rect = mobileToc.getBoundingClientRect();
  if (rect.height <= 0) return 0;

  const isPinned = rect.top <= stickyTop + 2;
  if (isPinned) {
    return Math.round(rect.bottom) + minGap;
  }

  const trigger = mobileToc.querySelector('[data-mobile-toc-trigger]') as HTMLElement | null;
  const collapsedHeight = trigger?.getBoundingClientRect().height ?? 64;
  return Math.round(stickyTop + collapsedHeight + 2) + minGap;
}

/** Live sticky offset below navbar/breadcrumbs. Shared by TOC scroll + sidebar shell. */
export function getStickyTopOffset(minGap = 16, extraClearance = 0): number {
  if (typeof document === 'undefined' || typeof window === 'undefined') return 96;

  const rootStyles = window.getComputedStyle(document.documentElement);
  const navbarTopInset = toPx(rootStyles.getPropertyValue('--navbar-top-inset'), 0);
  const configuredNavbarHeight = toPx(rootStyles.getPropertyValue('--navbar-height'), 64);

  let offset = Math.max(configuredNavbarHeight, 80) + navbarTopInset + minGap;

  const breadcrumbEl = document.querySelector('nav[aria-label="Breadcrumb"]');
  const headerEl = document.querySelector('header') as HTMLElement | null;

  if (breadcrumbEl) {
    const bcr = breadcrumbEl.getBoundingClientRect();
    if (bcr.bottom > 0) {
      offset = Math.max(offset, Math.round(bcr.bottom) + minGap);
    }
  } else if (headerEl) {
    const hdr = headerEl.getBoundingClientRect();
    if (hdr.height > 0 && hdr.top <= 8) {
      offset = Math.max(offset, Math.round(hdr.bottom) + minGap);
    }
  }

  const mobileTocClearance = getMobileArticleTocClearance(minGap);
  if (mobileTocClearance > offset) {
    offset = mobileTocClearance;
  }

  return Math.round(offset + extraClearance);
}

export function getTocScrollOffset(extraClearance = DEFAULT_TOC_CLEARANCE_PX): number {
  return getStickyTopOffset(16, extraClearance);
}

/** Viewport Y line used to pick the active TOC entry while scrolling. */
export function getTocActiveTriggerLine(scrollY = getCurrentScrollTop()): number {
  return scrollY + getTocScrollOffset() + 16;
}

/** Viewport-relative trigger line (for getBoundingClientRect().top comparisons). */
export function getTocActiveTriggerViewportY(): number {
  return getTocScrollOffset() + 16;
}

/** Read the scroll-margin used by BAIGF heading targets (single source of truth). */
export function getBestAIGirlfriendPageTocOffset(): number {
  if (typeof document === 'undefined') return getTocScrollOffset();

  const sample =
    document.getElementById('best-ai-girlfriend-overall') ??
    document.getElementById('best-ai-girlfriend-apps-websites-right-now') ??
    document.querySelector('.best-ai-girlfriend-page .toc-scroll-target[id]');

  if (sample instanceof HTMLElement) {
    const margin = toPx(getComputedStyle(sample).scrollMarginTop, 0);
    if (margin > 0) return Math.round(margin);
  }

  const stickyEl = document.querySelector(
    '.best-ai-girlfriend-page .best-ai-girlfriend-toc-sticky'
  ) as HTMLElement | null;
  if (stickyEl) {
    const topPx = toPx(getComputedStyle(stickyEl).top, 0);
    if (topPx > 0) return Math.round(topPx + 8);
  }

  return getTocScrollOffset();
}

/** Active trigger for BAIGF: just below the sticky TOC panel (matches reading position). */
export function getBestAIGirlfriendPageTocActiveTriggerY(): number {
  if (typeof document === 'undefined') return getTocScrollOffset();

  const stickyPanel = document.querySelector(
    '.best-ai-girlfriend-page .best-ai-girlfriend-toc-sticky'
  ) as HTMLElement | null;
  if (stickyPanel) {
    return Math.round(stickyPanel.getBoundingClientRect().bottom + 16);
  }

  return Math.round(window.innerHeight * 0.38);
}

export function getTocActiveTriggerViewportYForCurrentPage(): number {
  if (typeof document !== 'undefined' && document.querySelector('.best-ai-girlfriend-page')) {
    return getBestAIGirlfriendPageTocActiveTriggerY();
  }
  return getTocActiveTriggerViewportY();
}

function getCurrentScrollTop(): number {
  return window.pageYOffset || document.documentElement.scrollTop;
}

/** Keep CSS scroll-margin-top aligned with the live sticky header height. */
export function syncTocScrollOffsetCssVar(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const offset = getStickyTopOffset(16, 18);
  document.documentElement.style.setProperty('--toc-scroll-offset', `${offset}px`);
}

function getMaxScrollTop(): number {
  if (typeof document === 'undefined' || typeof window === 'undefined') return 0;
  return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
}

function scrollElementToTocTarget(element: HTMLElement, behavior: ScrollBehavior = 'auto'): void {
  syncTocScrollOffsetCssVar();
  const offset = getExpectedTargetViewportTop(element);
  const rect = element.getBoundingClientRect();
  const targetScroll = rect.top + getCurrentScrollTop() - offset;
  const clampedScroll = Math.min(Math.max(0, Math.round(targetScroll)), getMaxScrollTop());
  window.scrollTo({ top: clampedScroll, behavior });
}

function getExpectedTargetViewportTop(element: HTMLElement): number {
  const scrollMarginTop = toPx(window.getComputedStyle(element).scrollMarginTop, 0);
  return scrollMarginTop > 0 ? scrollMarginTop : getTocScrollOffset();
}

function getCorrectionDelays(options: TocScrollOptions): number[] {
  if (options.correctionDelaysMs) {
    return options.correctionDelaysMs;
  }

  const firstDelay = options.correctionDelayMs ?? 400;
  if (firstDelay <= 0) return [];

  return Array.from(new Set([firstDelay, 900, 1600, 2400]));
}

function cancelCorrectionsOnUserInput(sessionId: number, cleanupAfterMs: number): void {
  if (typeof window === 'undefined') return;

  let lastScrollTop = getCurrentScrollTop();

  const cancel = () => {
    if (sessionId === activeScrollSession) {
      activeScrollSession += 1;
    }
  };

  const onScroll = () => {
    const currentScrollTop = getCurrentScrollTop();
    if (Math.abs(currentScrollTop - lastScrollTop) > 2) {
      cancel();
    }
    lastScrollTop = currentScrollTop;
  };

  const options: AddEventListenerOptions = { passive: true };
  window.addEventListener('wheel', cancel, options);
  window.addEventListener('touchmove', cancel, options);
  window.addEventListener('keydown', cancel);
  window.addEventListener('scroll', onScroll, options);

  window.setTimeout(() => {
    window.removeEventListener('wheel', cancel);
    window.removeEventListener('touchmove', cancel);
    window.removeEventListener('keydown', cancel);
    window.removeEventListener('scroll', onScroll);
  }, cleanupAfterMs);
}

function snapToTargetIfNeeded(sessionId: number, targetId: string): void {
  if (sessionId !== activeScrollSession) return;

  const element = document.getElementById(targetId);
  if (!element) return;

  const offset = getExpectedTargetViewportTop(element);
  const rect = element.getBoundingClientRect();
  const maxScroll = getMaxScrollTop();
  const currentScroll = getCurrentScrollTop();
  const idealScroll = rect.top + currentScroll - offset;

  if (idealScroll >= maxScroll - POSITION_TOLERANCE_PX) {
    // Only snap to the document bottom during the initial jump, not after the user scrolls up.
    if (currentScroll >= maxScroll - POSITION_TOLERANCE_PX) return;
    if (currentScroll < maxScroll - 48) return;
    window.scrollTo({ top: maxScroll, behavior: 'auto' });
    return;
  }

  if (Math.abs(rect.top - offset) <= POSITION_TOLERANCE_PX) return;

  scrollElementToTocTarget(element, 'auto');
}

function scheduleScrollCorrections(sessionId: number, targetId: string, delaysMs: number[]): void {
  delaysMs.forEach((delayMs) => {
    window.setTimeout(() => snapToTargetIfNeeded(sessionId, targetId), delayMs);
  });

  if (typeof window === 'undefined') return;

  const onScrollEnd = () => {
    window.removeEventListener('scrollend', onScrollEnd);
    snapToTargetIfNeeded(sessionId, targetId);
  };

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', onScrollEnd, { once: true });
  }

  const lastDelay = delaysMs[delaysMs.length - 1] ?? 400;
  window.setTimeout(() => snapToTargetIfNeeded(sessionId, targetId), lastDelay + 120);
}

export function scrollToTocTarget(targetId: string, options: TocScrollOptions = {}): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;

  const targetElement = document.getElementById(targetId);
  if (!targetElement) return false;

  const correctionDelaysMs = getCorrectionDelays(options);
  const updateHash = options.updateHash ?? true;
  const sessionId = ++activeScrollSession;
  const lastCorrectionDelayMs = correctionDelaysMs[correctionDelaysMs.length - 1] ?? 0;

  scrollElementToTocTarget(targetElement, 'smooth');
  cancelCorrectionsOnUserInput(sessionId, lastCorrectionDelayMs + 300);
  scheduleScrollCorrections(sessionId, targetId, correctionDelaysMs);

  if (updateHash && typeof history?.replaceState === 'function') {
    history.replaceState(null, '', `#${targetId}`);
  }

  return true;
}

/** Collapse mobile TOC (if open), wait for layout, then scroll to the heading. */
export function scrollToArticleTocTarget(targetId: string, options: TocScrollOptions = {}): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (!document.getElementById(targetId)) return false;

  const mobileTocTrigger = document.querySelector(
    '.mobile-toc [data-mobile-toc-trigger][aria-expanded="true"]'
  ) as HTMLButtonElement | null;
  if (mobileTocTrigger) {
    mobileTocTrigger.click();
  }

  const run = () => scrollToTocTarget(targetId, options);

  if (mobileTocTrigger && isMobileArticleTocViewport()) {
    requestAnimationFrame(() => requestAnimationFrame(run));
    return true;
  }

  return run();
}

const DEFAULT_HASH_CORRECTION_DELAYS_MS = [300, 800, 1500, 2500];

function getNavigationType(): PerformanceNavigationTiming['type'] | undefined {
  if (typeof performance === 'undefined') return undefined;
  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return entry?.type;
}

export function isPageReload(): boolean {
  return getNavigationType() === 'reload';
}

/** Full reloads should open at the top, not a stale TOC hash or restored scroll position. */
export function ensurePageStartsAtTopOnReload(): void {
  if (typeof window === 'undefined' || !isPageReload()) return;

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  window.scrollTo(0, 0);

  if (window.location.hash) {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
}

/** Scroll to the URL hash on load, with retries for late-rendered heading ids. */
export function scrollToTocHashOnLoad(
  correctionDelaysMs: number[] = DEFAULT_HASH_CORRECTION_DELAYS_MS
): () => void {
  if (typeof window === 'undefined') return () => {};

  ensurePageStartsAtTopOnReload();
  if (isPageReload()) return () => {};

  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return () => {};

  const scrollToHash = () => {
    if (document.getElementById(hash)) {
      scrollToTocTarget(hash, { updateHash: false, correctionDelaysMs });
    }
  };

  scrollToHash();
  const t1 = window.setTimeout(scrollToHash, 300);
  const t2 = window.setTimeout(scrollToHash, 1200);
  const t3 = window.setTimeout(scrollToHash, 1800);

  return () => {
    window.clearTimeout(t1);
    window.clearTimeout(t2);
    window.clearTimeout(t3);
  };
}

function getTocHeadingViewportTop(element: HTMLElement): number {
  return element.getBoundingClientRect().top;
}

/** Pick the heading for the section currently being read. */
export function findActiveTocHeadingId(
  headings: Array<{ id: string }>,
  scrollY = getCurrentScrollTop(),
  options?: { triggerViewportY?: number }
): string | null {
  if (headings.length === 0) return null;

  const triggerViewportY = options?.triggerViewportY ?? getTocActiveTriggerViewportYForCurrentPage();
  let best: string | null = null;
  let bestTop = -Infinity;

  for (const { id } of headings) {
    const el = document.getElementById(id);
    if (!el) continue;
    const top = getTocHeadingViewportTop(el);
    if (top <= triggerViewportY && top > bestTop) {
      bestTop = top;
      best = id;
    }
  }

  if (best) return best;

  // Above first section: no heading passed the line yet.
  const firstEl = document.getElementById(headings[0].id);
  if (firstEl && getTocHeadingViewportTop(firstEl) > triggerViewportY) {
    return null;
  }

  if (typeof window !== 'undefined') {
    const maxScroll = getMaxScrollTop();
    if (scrollY >= maxScroll - POSITION_TOLERANCE_PX) {
      return headings[headings.length - 1].id;
    }
  }

  return headings[0]?.id ?? null;
}

/** Scroll a TOC list container so an item is visible, without moving the page. */
export function scrollTocListItemIntoView(
  listEl: HTMLElement,
  itemEl: HTMLElement,
  behavior: ScrollBehavior = 'auto'
): void {
  const listRect = listEl.getBoundingClientRect();
  const itemRect = itemEl.getBoundingClientRect();
  const edgePadding = 12;

  if (
    itemRect.top >= listRect.top + edgePadding &&
    itemRect.bottom <= listRect.bottom - edgePadding
  ) {
    return;
  }

  if (itemRect.top < listRect.top) {
    listEl.scrollTo({
      top: listEl.scrollTop + (itemRect.top - listRect.top),
      behavior,
    });
    return;
  }

  listEl.scrollTo({
    top: listEl.scrollTop + (itemRect.bottom - listRect.bottom),
    behavior,
  });
}
